
import { Component, Prop, Vue, Watch } from "vue-property-decorator";
import Moment from "moment";
import httpClient from "@/store/httpClient";
import rules from "@/store/rules";
import { CatalogueSortingType } from "@/dtos/catalogueSortingType";
import DatePicker from "@/views/Settings/DatePicker.vue";
import { AddressDto } from "@/dtos/addressDto";
import { FertigerAddressDto } from "@/dtos/fertigerAddressDto";
import { CartItem } from "@/dtos/cartItem";
import { ArticleState } from "@/dtos/articleState";
import { CustomerArticleDto } from "@/dtos/customerArticleDto";
import { OfferRequestDto } from "@/dtos/offerRequestDto";
import { OfferRequestPositionDto } from "@/dtos/offerRequestPositionDto";
import { OfferRequestCallOffInterval } from "@/dtos/offerRequestCallOffInterval";

@Component({
	components: {
		DatePicker,
	},
})
export default class CreateOfferRequest extends Vue {
	/** Id of the draft being edited. Zero creates a new request from the cart. */
	@Prop({ default: 0 })
	private requestId!: number;

	private request: OfferRequestDto = new OfferRequestDto();
	private isLoadingRequest: boolean = false;

	// Auto save state. The draft is written in the background after every edit.
	private autoSaveArmed: boolean = false;
	private autoSaveTimer: number | null = null;
	private isSavingDraft: boolean = false;
	private saveDraftAgain: boolean = false;
	private draftSaveFailed: boolean = false;
	private draftSavedAt: string = "";
	private deleteDialogOpen: boolean = false;
	private isDeleting: boolean = false;
	private positionsExpanded: boolean = true;
	private articlePickerOpen: boolean = false;
	private articleSearchTerm: string = "";
	private pickerArticles: CustomerArticleDto[] = new Array<CustomerArticleDto>();
	private pickerBatchSize: number = 50;
	private isLoadingArticles: boolean = false;
	private searchDebounceTimer: number | null = null;

	private quantityRules = rules.allowedCartAmountRule;
	// Bounded so it still fits the column and the tile.
	private titleRules = rules.requiredStringMax50Rule;
	private intervalCountRules = [
		(v: number) => !!v || (this.$t("store.rules.required") as string),
		(v: number) => !isNaN(v) || (this.$t("store.rules.notANumber") as string),
		(v: number) => v % 1 === 0 || (this.$t("store.rules.noDecimals") as string),
		(v: number) => v >= 2 || (this.$t("offerRequest.form.orderType.callOffCountMin") as string),
	];
	private callOffCountRules = [
		(v: number) => !!v || (this.$t("store.rules.required") as string),
		(v: number) => !isNaN(v) || (this.$t("store.rules.notANumber") as string),
		(v: number) => v % 1 === 0 || (this.$t("store.rules.noDecimals") as string),
		(v: number) => v >= 2 || (this.$t("offerRequest.form.orderType.callOffCountMin") as string),
	];
	/**
	 * UI state only: false means every week or month, true means every n of them.
	 * What gets stored is the resulting callOffIntervalCount.
	 */
	private useCustomIntervalCount: boolean = false;
	/**
	 * UI state only: false means the series runs until it is revoked, true means a
	 * fixed number of deliveries. What gets stored is the resulting callOffCount.
	 */
	private hasLimitedCallOffs: boolean = false;
	/**
	 * UI state only. The request stores just the delivery address, and an address of
	 * zero already means the customer collects the order.
	 */
	private isDelivery: boolean = true;
	/** Guards against a second submit while the first one is still running. */
	private isSubmitting: boolean = false;
	/** Manufacturers that can produce all articles and offer collection, shown by their town. */
	private pickupLocations: { value: number; text: string }[] = [];
	private isLoadingPickupLocations: boolean = false;
	private pickupLocationsFailed: boolean = false;
	private pickupCheckTimer: number | null = null;
	/** Counts the checks, so the answer of an outdated check is dropped. */
	private pickupCheckCount: number = 0;

	private mounted() {
		if (this.requestId > 0) {
			this.loadRequest();
		} else {
			this.copyPositionsFromCart();
			this.presetDeliveryAddress();
			this.armAutoSave();
		}
	}

	/**
	 * Loads an existing draft and rebuilds the UI state that the request itself does
	 * not store: whether an inspection is wanted, which positions carry a tick,
	 * delivery versus collection, and a plain versus a stretched call-off rhythm.
	 */
	private async loadRequest() {
		this.isLoadingRequest = true;
		try {
			const response = await httpClient().get(`Offer/GetOfferRequest?id=${this.requestId}`);
			const loaded = Object.assign(new OfferRequestDto(), response.data);
			loaded.positions = (response.data.positions || []).map((p: any) =>
				Object.assign(new OfferRequestPositionDto(), p, {
					isSelectedForInspection: p.inspectionQuantity > 0,
				})
			);
			this.request = loaded;
			this.isDelivery = !!loaded.deliveryAddressId;
			this.useCustomIntervalCount = loaded.callOffIntervalCount > 1;
			this.hasLimitedCallOffs = loaded.callOffCount > 0;
			this.request.wantsFirstArticleInspection = this.positions.some(
				(p: OfferRequestPositionDto) => p.inspectionQuantity > 0
			);
			// Article names and thumbnails come from the store, and the store only
			// holds what another view happened to load.
			await this.$store.dispatch(
				"loadMissingArticles",
				this.positions.map((p: OfferRequestPositionDto) => p.articleId)
			);
		} catch (e) {
			console.error("Failed to load the offer request.", e);
			this.$store.dispatch("setSnackbarErrorText", this.$t("offerRequest.form.loadFailed"));
			this.$router.push({ name: "OfferRequests" });
		} finally {
			this.isLoadingRequest = false;
			this.armAutoSave();
		}
	}

	// ---------------------------------------------------------------- enums
	// Exposed so the template can name the values instead of using raw numbers.
	private get callOffIntervals() {
		return OfferRequestCallOffInterval;
	}

	// -------------------------------------------------------------- call-offs
	/**
	 * Drives the order type radios. Switching to a series preselects a monthly
	 * rhythm, so the customer starts from the common case. Switching back to a
	 * single order is expressed by the rhythm itself.
	 */
	private get isRecurringOrder(): boolean {
		return this.request.callOffInterval !== OfferRequestCallOffInterval.Once;
	}

	private set isRecurringOrder(value: boolean) {
		if (value === this.isRecurringOrder) {
			return;
		}
		this.request.callOffInterval = value
			? OfferRequestCallOffInterval.Monthly
			: OfferRequestCallOffInterval.Once;
		this.request.callOffIntervalCount = 1;
		this.useCustomIntervalCount = false;
		// A single order has its one delivery, a series runs until it is revoked.
		this.request.callOffCount = value ? 0 : 1;
		this.hasLimitedCallOffs = false;
	}

	/** Only a weekly or monthly rhythm can be stretched over several intervals. */
	private get hasIntervalCount(): boolean {
		return (
			this.request.callOffInterval === OfferRequestCallOffInterval.Weekly ||
			this.request.callOffInterval === OfferRequestCallOffInterval.Monthly
		);
	}

	private get isWeekly(): boolean {
		return this.request.callOffInterval === OfferRequestCallOffInterval.Weekly;
	}

	private get everySingleIntervalLabel(): string {
		return this.isWeekly
			? this.$t("offerRequest.form.orderType.callOffEveryWeek").toString()
			: this.$t("offerRequest.form.orderType.callOffEveryMonth").toString();
	}

	private get everyNthIntervalLabel(): string {
		return this.isWeekly
			? this.$t("offerRequest.form.orderType.callOffEveryNWeeks").toString()
			: this.$t("offerRequest.form.orderType.callOffEveryNMonths").toString();
	}

	private get intervalCountSuffix(): string {
		return this.isWeekly
			? this.$t("offerRequest.form.orderType.callOffWeeksUnit").toString()
			: this.$t("offerRequest.form.orderType.callOffMonthsUnit").toString();
	}

	// A fresh rhythm starts at every week or month. Yearly has no count at all.
	private onCallOffIntervalChanged() {
		this.useCustomIntervalCount = false;
		this.request.callOffIntervalCount = 1;
	}

	private onIntervalCountModeChanged() {
		this.request.callOffIntervalCount = this.useCustomIntervalCount ? 2 : 1;
	}

	// Zero is the series without an end. A limited one needs at least two deliveries,
	// a single one is not a series.
	private onCallOffCountModeChanged() {
		this.request.callOffCount = this.hasLimitedCallOffs ? 2 : 0;
	}

	/**
	 * When the last delivery would arrive, counted from the requested completion of the
	 * first one. Empty as long as the completion date or the number of deliveries is
	 * missing, and for a yearly rhythm the interval count is always one.
	 */
	private get lastDeliveryText(): string {
		const completion = this.request.completionTo || this.request.completionFrom;
		const deliveries = this.request.callOffCount;
		if (!completion || !deliveries || deliveries < 2) {
			return "";
		}
		const intervals = (deliveries - 1) * (this.request.callOffIntervalCount || 1);
		const unit = this.isWeekly ? "weeks" : this.isYearly ? "years" : "months";
		const lastDelivery = Moment(completion).add(intervals, unit);
		return this.$t("offerRequest.form.orderType.callOffLastDelivery", {
			date: lastDelivery.format("DD.MM.YYYY"),
		}).toString();
	}

	private get isYearly(): boolean {
		return this.request.callOffInterval === OfferRequestCallOffInterval.Yearly;
	}

	// ------------------------------------------------------------ positions
	private get positions(): OfferRequestPositionDto[] {
		return this.request.positions;
	}

	private get totalQuantity(): number {
		return this.positions.reduce(
			(sum: number, position: OfferRequestPositionDto) => sum + (position.quantity || 0),
			0
		);
	}

	/**
	 * Takes over the articles and quantities of the cart once. From here on the
	 * request owns its positions, so later edits never touch the cart.
	 */
	private copyPositionsFromCart() {
		const cart = this.$store.state.currentCart;
		if (cart === null || cart === undefined) {
			return;
		}
		this.request.positions = cart.cartItems.map((item: CartItem) => {
			const position = new OfferRequestPositionDto();
			position.articleId = item.articleId;
			position.quantity = item.quantity;
			return position;
		});
	}

	private removePosition(position: OfferRequestPositionDto) {
		const index = this.positions.indexOf(position);
		if (index >= 0) {
			this.positions.splice(index, 1);
		}
	}

	private addPosition(article: CustomerArticleDto) {
		const position = new OfferRequestPositionDto();
		position.articleId = article.articleId;
		position.quantity = 1;
		// While every position is being sampled, a new one joins the inspection too.
		position.isSelectedForInspection =
			this.wantsInspection && (this.positions.length === 0 || this.allSelectedForInspection);
		this.positions.push(position);
	}

	// -------------------------------------------------------------- articles
	private get allArticles(): CustomerArticleDto[] {
		return this.$store.state.articles as CustomerArticleDto[];
	}

	private article(articleId: number): CustomerArticleDto | undefined {
		return this.allArticles.find((a: CustomerArticleDto) => a.articleId === articleId);
	}

	private articleName(articleId: number): string {
		const article = this.article(articleId);
		if (article === undefined) {
			return this.$t("offerRequest.form.positions.articleNumber", {
				id: articleId,
			}).toString();
		}
		return article.customerArticleName || article.customerArticleId;
	}

	private articleMeta(articleId: number): string {
		const article = this.article(articleId);
		if (article === undefined) {
			return "";
		}
		const number = this.$t("offerRequest.form.positions.articleNumber", { id: articleId });
		if (article.isAssembly) {
			return number.toString();
		}
		return `${number} · ${article.materialname}`;
	}

	// The article's lastChange is passed on purpose: without it the getter stamps the
	// current time into the url, so every re-render would request the image again.
	private thumbnailUrl(articleId: number): string {
		const article = this.article(articleId);
		return this.$store.getters.getThumbnailUrl(
			articleId,
			article ? article.lastChange : undefined
		);
	}

	/** Orderable articles from the last search that are not part of the request yet. */
	private get selectableArticles(): CustomerArticleDto[] {
		return this.pickerArticles.filter((article: CustomerArticleDto) => {
			if (!article.canBeProduced || article.calculating || article.parentArticleId > 0) {
				return false;
			}
			// Only an article that can be produced as it is belongs in a request.
			// A warning is fine, everything else has to be fixed in the catalogue first.
			if (article.state !== ArticleState.OK && article.state !== ArticleState.Warning) {
				return false;
			}
			// A migrated article has to be recalculated first, so its price is not
			// dependable and it has no place in a request for a manual offer.
			if (article.migrated) {
				return false;
			}
			return !this.positions.some(
				(p: OfferRequestPositionDto) => p.articleId === article.articleId
			);
		});
	}

	@Watch("articlePickerOpen")
	private onArticlePickerToggled(isOpen: boolean) {
		if (isOpen) {
			this.articleSearchTerm = "";
			this.loadSelectableArticles();
		}
	}

	@Watch("articleSearchTerm")
	private onArticleSearchTermChanged() {
		if (this.searchDebounceTimer !== null) {
			clearTimeout(this.searchDebounceTimer);
		}
		this.searchDebounceTimer = window.setTimeout(() => this.loadSelectableArticles(), 400);
	}

	/**
	 * Searches the customer's articles on the server. Going to the server keeps the picker
	 * complete: the store only holds the articles another view happened to load. Terms
	 * shorter than three characters are sent as an empty search, as in the catalogue.
	 */
	private async loadSelectableArticles() {
		const term = (this.articleSearchTerm || "").trim();
		const relevantTerm = term.length >= 3 ? term : "";
		this.isLoadingArticles = true;
		try {
			const response = await httpClient().get(
				`article/getArticlesOfCustomer?searchTerm=${encodeURI(relevantTerm)}` +
					`&from=0&to=${this.pickerBatchSize}` +
					`&sorting=${CatalogueSortingType.ArticleNameAscending}&userId=`
			);
			response.data.forEach((article: any) => (article.calculatingMessage = ""));
			// Also kept in the store so the name of an added position resolves right away.
			this.$store.commit("addOrUpdateArticles", response.data);
			this.pickerArticles = response.data;
		} catch (e) {
			console.error("Failed to load articles for the offer request picker.", e);
			this.pickerArticles = new Array<CustomerArticleDto>();
		} finally {
			this.isLoadingArticles = false;
		}
	}

	// ---------------------------------------------------------------- order type
	/** Editing an existing draft says so, creating a new one says new. */
	/** Wording of the background save indicator in the page header. */
	private get draftStateText(): string {
		if (this.draftSaveFailed) {
			return this.$t("offerRequest.form.draftSaveFailed").toString();
		}
		if (this.isSavingDraft) {
			return this.$t("offerRequest.form.draftSaving").toString();
		}
		if (this.draftSavedAt) {
			return this.$t("offerRequest.form.draftSaved", { time: this.draftSavedAt }).toString();
		}
		return this.$t("offerRequest.form.draftHint").toString();
	}

	private get draftStateIcon(): string {
		if (this.draftSaveFailed) {
			return "mdi-alert-circle-outline";
		}
		return this.isSavingDraft ? "mdi-progress-upload" : "mdi-cloud-check-outline";
	}

	/**
	 * Once the request is sent it is with Blexon, and what the customer agreed to must not
	 * drift afterwards. It stays open to look at, with every field and every action locked.
	 */
	private get isReadonly(): boolean {
		return this.request.submittedAt !== null;
	}

	private get pageTitle(): string {
		if (this.isReadonly) {
			return this.$t("offerRequest.form.viewTitle").toString();
		}
		return this.requestId > 0
			? this.$t("offerRequest.form.editTitle").toString()
			: this.$t("offerRequest.form.pageTitle").toString();
	}

	private get isOneTime(): boolean {
		return this.request.callOffInterval === OfferRequestCallOffInterval.Once;
	}

	/** For a series the window is about the first delivery, so the heading differs. */
	private get completionTitle(): string {
		return this.isOneTime
			? this.$t("offerRequest.form.orderType.completionTitle").toString()
			: this.$t("offerRequest.form.orderType.completionTitleFirstDelivery").toString();
	}

	/** Earliest date we let the customer pick, matching the usual production lead time. */
	private get earliestCompletionDate(): string {
		return Moment().add(21, "day").format("YYYY-MM-DD");
	}

	// The request keeps real dates, the picker speaks ISO day strings. These two pairs
	// translate between them.
	private get completionFromValue(): string {
		return this.toPickerValue(this.request.completionFrom);
	}

	private get completionToValue(): string {
		return this.toPickerValue(this.request.completionTo);
	}

	private setCompletionFrom(value: string) {
		this.request.completionFrom = this.toDate(value);
		// A window needs an end. Proposing one saves the second pick in the common
		// case, and the customer can still widen it.
		if (this.request.completionFrom && !this.request.completionTo) {
			this.request.completionTo = this.addWorkingDays(this.request.completionFrom, 2);
		}
	}

	/**
	 * Adds working days, skipping Saturdays and Sundays. Public holidays are not
	 * known in the client, so they are not taken into account.
	 */
	private addWorkingDays(start: Date, days: number): Date {
		const result = Moment.utc(start);
		let added = 0;
		while (added < days) {
			result.add(1, "day");
			if (result.isoWeekday() <= 5) {
				added++;
			}
		}
		return result.toDate();
	}

	private setCompletionTo(value: string) {
		this.request.completionTo = this.toDate(value);
	}

	// Both directions run in UTC on purpose. A completion window is a calendar day,
	// not a moment in time. Building it at local midnight would serialise to the
	// previous day east of Greenwich and shift the date the customer picked.
	private toPickerValue(value: Date | null): string {
		return value ? Moment.utc(value).format("YYYY-MM-DD") : "";
	}

	private toDate(value: string): Date | null {
		return value ? Moment.utc(value).toDate() : null;
	}

	/** The end of the window can never be before its start. */
	private get completionToMinDate(): string {
		return this.completionFromValue || this.earliestCompletionDate;
	}

	// ---------------------------------------------------------------- delivery
	private get deliveryAddresses(): AddressDto[] {
		const customer = this.$store.state.customer;
		if (customer === null || customer === undefined) {
			return new Array<AddressDto>();
		}
		return (customer.deliveryAddresses as AddressDto[]) || new Array<AddressDto>();
	}

	private get deliveryAddressItems() {
		return this.deliveryAddresses.map((address: AddressDto) => ({
			value: address.id,
			text: `${address.companyName || address.surname}, ${address.street}, ${address.zip} ${
				address.town
			}`,
		}));
	}

	private presetDeliveryAddress() {
		const preferred = this.$store.getters.userPreferredDeliveryAddress as AddressDto;
		if (preferred && preferred.id > 0) {
			this.request.deliveryAddressId = preferred.id;
			return;
		}
		if (this.deliveryAddresses.length > 0) {
			this.request.deliveryAddressId = this.deliveryAddresses[0].id;
		}
	}

	/**
	 * Collection carries no address and no note, so both are dropped when chosen. A delivery
	 * has no pickup location.
	 */
	private onDeliveryTypeChanged() {
		if (!this.isDelivery) {
			this.request.deliveryAddressId = null;
			this.request.deliveryNote = "";
		} else {
			this.request.selectedFertigerId = null;
			if (!this.request.deliveryAddressId) {
				this.presetDeliveryAddress();
			}
		}
	}

	/** Changes only when an article is added or removed, not when a quantity changes. */
	private get positionArticleIds(): string {
		return this.positions
			.map((p: OfferRequestPositionDto) => p.articleId)
			.sort((a: number, b: number) => a - b)
			.join(",");
	}

	@Watch("isDelivery")
	private onIsDeliveryChanged() {
		this.schedulePickupCheck();
	}

	@Watch("positionArticleIds")
	private onPositionArticleIdsChanged() {
		this.schedulePickupCheck();
	}

	/** Collection only. Adding several articles in a row runs a single check. */
	private schedulePickupCheck() {
		if (this.pickupCheckTimer !== null) {
			clearTimeout(this.pickupCheckTimer);
			this.pickupCheckTimer = null;
		}
		if (this.isDelivery) {
			return;
		}
		this.pickupCheckTimer = window.setTimeout(() => this.checkPickupLocations(), 400);
	}

	/**
	 * Asks which manufacturers can produce all articles together and offers those that offer
	 * collection. A chosen location that is no longer possible is dropped.
	 */
	private async checkPickupLocations() {
		const checkCount = ++this.pickupCheckCount;
		const articleIds = this.positions.map((p: OfferRequestPositionDto) => p.articleId);
		if (articleIds.length === 0) {
			this.pickupLocations = [];
			this.isLoadingPickupLocations = false;
			return;
		}
		this.isLoadingPickupLocations = true;
		this.pickupLocationsFailed = false;
		try {
			const response = await httpClient().post(
				"Offer/GetPossibleManufacturerIds",
				articleIds
			);
			if (checkCount !== this.pickupCheckCount) {
				return;
			}
			const manufacturerIds = response.data as number[];
			this.pickupLocations = (this.$store.state.manufacturerAddresses as FertigerAddressDto[])
				.filter(
					(address: FertigerAddressDto) =>
						address.isRampenabholungAvailable &&
						manufacturerIds.includes(address.manufacturerId)
				)
				.map((address: FertigerAddressDto) => ({
					value: address.manufacturerId,
					text: address.town,
				}))
				.sort((a, b) => a.text.localeCompare(b.text));
			const selectedId = this.request.selectedFertigerId;
			if (
				selectedId !== null &&
				!this.pickupLocations.some((location) => location.value === selectedId)
			) {
				this.request.selectedFertigerId = null;
			}
		} catch (e) {
			if (checkCount !== this.pickupCheckCount) {
				return;
			}
			console.error("Failed to check the pickup locations.", e);
			this.pickupLocationsFailed = true;
		} finally {
			if (checkCount === this.pickupCheckCount) {
				this.isLoadingPickupLocations = false;
			}
		}
	}

	// -------------------------------------------------------------- inspection
	/** Drives the two extra columns of the position list. */
	private get wantsInspection(): boolean {
		return this.request.wantsFirstArticleInspection;
	}

	// Turning the inspection on samples everything, which is the usual case. The
	// customer then unticks what should stay out of it.
	private onInspectionWantedChanged() {
		if (this.request.wantsFirstArticleInspection) {
			this.toggleSelectAll(true);
		} else {
			this.clearInspectionSelection();
		}
	}

	private get allSelectedForInspection(): boolean {
		return (
			this.positions.length > 0 &&
			this.positions.every((p: OfferRequestPositionDto) => p.isSelectedForInspection)
		);
	}

	private toggleSelectAll(selectAll: boolean) {
		this.positions.forEach((position: OfferRequestPositionDto) => {
			position.isSelectedForInspection = selectAll === true;
			position.inspectionQuantity = position.isSelectedForInspection ? 1 : 0;
		});
	}

	// A position that is not sampled carries no sample quantity.
	private onPositionInspectionToggled(position: OfferRequestPositionDto, isSelected: boolean) {
		position.inspectionQuantity = isSelected ? 1 : 0;
	}

	private clearInspectionSelection() {
		this.positions.forEach((position: OfferRequestPositionDto) => {
			position.isSelectedForInspection = false;
			position.inspectionQuantity = 0;
		});
	}

	private get selectedInspectionPositions(): OfferRequestPositionDto[] {
		return this.positions.filter((p: OfferRequestPositionDto) => p.isSelectedForInspection);
	}

	/** A sample can never be larger than the quantity the offer is requested for. */
	private inspectionQuantityRules(position: OfferRequestPositionDto) {
		return [
			(v: number) => !!v || (this.$t("store.rules.required") as string),
			(v: number) => !isNaN(v) || (this.$t("store.rules.notANumber") as string),
			(v: number) => v % 1 === 0 || (this.$t("store.rules.noDecimals") as string),
			(v: number) => v >= 1 || (this.$t("store.rules.mustBePositive") as string),
			(v: number) =>
				v <= (position.quantity || 0) ||
				(this.$t("offerRequest.form.inspection.quantityTooHigh", {
					max: position.quantity,
				}) as string),
		];
	}

	// -------------------------------------------------------------- validation
	/** Named reasons why the request cannot be sent yet. Empty means ready to send. */
	private get blockingReasons(): string[] {
		const reasons = new Array<string>();
		if (!this.request.title || this.request.title.trim().length === 0) {
			reasons.push(this.$t("offerRequest.form.blockers.title").toString());
		}
		if (this.positions.length === 0) {
			reasons.push(this.$t("offerRequest.form.blockers.positions").toString());
		}
		if (!this.request.completionFrom || !this.request.completionTo) {
			reasons.push(this.$t("offerRequest.form.blockers.completionWindow").toString());
		}
		if (
			this.request.completionFrom &&
			this.request.completionTo &&
			this.request.completionTo.getTime() < this.request.completionFrom.getTime()
		) {
			reasons.push(this.$t("offerRequest.form.blockers.completionOrder").toString());
		}
		if (
			!this.isOneTime &&
			this.useCustomIntervalCount &&
			(!this.request.callOffIntervalCount ||
				this.request.callOffIntervalCount < 2 ||
				this.request.callOffIntervalCount % 1 !== 0)
		) {
			reasons.push(this.$t("offerRequest.form.blockers.callOffIntervalCount").toString());
		}
		if (
			!this.isOneTime &&
			this.hasLimitedCallOffs &&
			(!this.request.callOffCount ||
				this.request.callOffCount < 2 ||
				this.request.callOffCount % 1 !== 0)
		) {
			reasons.push(this.$t("offerRequest.form.blockers.callOffCount").toString());
		}
		if (
			this.isDelivery &&
			this.deliveryAddresses.length > 0 &&
			!this.request.deliveryAddressId
		) {
			reasons.push(this.$t("offerRequest.form.blockers.address").toString());
		}
		if (!this.isDelivery && !this.request.selectedFertigerId) {
			reasons.push(this.$t("offerRequest.form.blockers.pickupLocation").toString());
		}
		if (this.wantsInspection && this.selectedInspectionPositions.length === 0) {
			reasons.push(this.$t("offerRequest.form.blockers.inspectionPositions").toString());
		}
		if (
			this.wantsInspection &&
			this.selectedInspectionPositions.some(
				(p: OfferRequestPositionDto) =>
					!p.inspectionQuantity ||
					p.inspectionQuantity < 1 ||
					p.inspectionQuantity % 1 !== 0
			)
		) {
			reasons.push(this.$t("offerRequest.form.blockers.inspectionQuantity").toString());
		}
		return reasons;
	}

	// --------------------------------------------------------------- auto save
	/**
	 * Every edit is stored as a draft in the background. The first edit of a new
	 * request creates it, later edits update it. Filling the form from the cart or
	 * loading an existing draft is not an edit, so autoSaveArmed stays false until
	 * that initial work is done.
	 */
	@Watch("request", { deep: true })
	private onRequestChanged() {
		// Sending tidies a few fields up, and those changes must not schedule a draft
		// save that the server would refuse once the request is sent.
		if (!this.autoSaveArmed || this.isReadonly || this.isSubmitting) {
			return;
		}
		if (this.autoSaveTimer !== null) {
			clearTimeout(this.autoSaveTimer);
		}
		this.autoSaveTimer = window.setTimeout(() => this.saveDraft(), 800);
	}

	/** Arms auto save once the initial values are in place. */
	private armAutoSave() {
		this.$nextTick(() => {
			this.autoSaveArmed = true;
		});
	}

	/**
	 * Runs the changes we suppress auto save for, for example writing back the id the
	 * server assigned. Without this the write would schedule another save at once.
	 */
	private withoutAutoSave(change: () => void) {
		this.autoSaveArmed = false;
		change();
		this.armAutoSave();
	}

	private async saveDraft() {
		this.autoSaveTimer = null;
		if (this.isSavingDraft) {
			// A change arrived while the previous save was still running.
			this.saveDraftAgain = true;
			return;
		}
		this.isSavingDraft = true;
		this.draftSaveFailed = false;
		try {
			const response = await httpClient().post("Offer/SaveOfferRequest", this.request);
			this.withoutAutoSave(() => (this.request.id = response.data));
			this.draftSavedAt = Moment().format("HH:mm");
		} catch (e) {
			console.error("Failed to save the offer request draft.", e);
			this.draftSaveFailed = true;
		} finally {
			this.isSavingDraft = false;
			if (this.saveDraftAgain) {
				this.saveDraftAgain = false;
				this.saveDraft();
			}
		}
	}

	/** A pending change must not be lost when the customer leaves the page. */
	private beforeDestroy() {
		if (this.autoSaveTimer !== null) {
			clearTimeout(this.autoSaveTimer);
			this.autoSaveTimer = null;
			if (!this.isReadonly && !this.isSubmitting) {
				this.saveDraft();
			}
		}
	}

	/**
	 * Removes the draft and its positions. Auto save is disarmed first, otherwise a
	 * pending change would create the request again right after it was deleted.
	 */
	private async deleteRequest() {
		if (this.isDeleting) {
			return;
		}
		if (this.autoSaveTimer !== null) {
			clearTimeout(this.autoSaveTimer);
			this.autoSaveTimer = null;
		}
		this.autoSaveArmed = false;
		this.isDeleting = true;
		try {
			await httpClient().post(`Offer/DeleteOfferRequest?id=${this.request.id}`);
			this.$store.dispatch("setSnackbarText", this.$t("offerRequest.form.deleted"));
			this.deleteDialogOpen = false;
			this.$router.push({ name: "OfferRequests" });
		} catch (e) {
			console.error("Failed to delete the offer request.", e);
			this.autoSaveArmed = true;
			this.$store.dispatch("setSnackbarErrorText", this.$t("offerRequest.form.deleteFailed"));
		} finally {
			this.isDeleting = false;
		}
	}

	// ------------------------------------------------------------------ submit
	/**
	 * Hands the request over to Blexon. The server stores it once more and sends it in
	 * the same step, and refuses it when it was sent already, for example from a second tab.
	 */
	private async submit() {
		if (this.blockingReasons.length > 0 || this.isSubmitting) {
			return;
		}
		// Set before the fields below change, so the watcher does not schedule a draft save.
		this.isSubmitting = true;
		if (this.autoSaveTimer !== null) {
			clearTimeout(this.autoSaveTimer);
			this.autoSaveTimer = null;
		}
		// Collection is expressed by the address staying empty.
		if (!this.isDelivery) {
			this.request.deliveryAddressId = null;
			this.request.deliveryNote = "";
		}
		// A single order and a yearly rhythm never span several intervals.
		if (!this.hasIntervalCount) {
			this.request.callOffIntervalCount = 1;
		}
		// A single order is exactly one delivery, whatever the field showed before.
		if (this.isOneTime) {
			this.request.callOffCount = 1;
		}
		try {
			const response = await httpClient().post("Offer/SubmitOfferRequest", this.request);
			this.request.id = response.data;
			this.$store.dispatch("setSnackbarText", this.$t("offerRequest.form.submitted"));
			this.$router.push({ name: "OfferRequests" });
		} catch (e) {
			console.error("Failed to submit the offer request.", e);
			this.$store.dispatch("setSnackbarErrorText", this.$t("offerRequest.form.submitFailed"));
		} finally {
			this.isSubmitting = false;
		}
	}
}
